import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export const DISCARD_REASONS = [
  { value: 1, label: "Expired" },
  { value: 2, label: "Damaged" },
  { value: 3, label: "Overgrown" },
  { value: 4, label: "Poor Quality" },
  { value: 5, label: "Contaminated" },
  { value: 6, label: "Pest" },
  { value: 7, label: "Disease" },
  { value: 99, label: "Other" },
] as const;

export interface CreateProductionDiscardDto {
  // Exactly one of these must be set. The greenhouse pipeline targets a
  // SowingInstruction; agroforestry write-offs target a GreenhouseStockEntry directly.
  sowingInstructionId?: string;
  greenhouseStockEntryId?: string;
  quantityToDiscard: number;
  reason: number;
  reasonDetails?: string;
  requiresApproval: boolean;
  greenhouseBedIds?: string[];
  locationNotes?: string;
  disposalMethod?: string;
}

export interface ApproveProductionDiscardDto {
  discardRequestId: string;
  isApproved: boolean;
  approvalNotes?: string;
}

export interface GreenhouseDiscardAvailabilityDto {
  sowingInstructionId: string;
  physicalQuantity: number;
  reservedQuantity: number;
  fillingReserve: number;
  availableQuantity: number;
  pendingDiscardQuantity: number;
  discardableQuantity: number;
  unit: number;
}

export interface PagedProductionDiscards {
  items: ProductionDiscardDto[];
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
}

export interface ProductionDiscardDto {
  id: string;
  /** Null on agroforestry-targeted discards. */
  sowingInstructionId?: string;
  /** Set when the discard targets an Agroforestry GreenhouseStockEntry directly. */
  greenhouseStockEntryId?: string;
  instructionNumber?: string;
  productionBatchNumber?: string;
  cropType: string;
  variety?: string;
  quantityToDiscard: number;
  unit: number;
  reason: number;
  reasonName: string;
  reasonDetails?: string;
  status: number;
  statusName: string;
  requiresApproval: boolean;
  requestedByUserId: string;
  requestedByUserName: string;
  requestedAt: string;
  approvedByUserId?: string;
  approvedByUserName?: string;
  approvedAt?: string;
  approvalNotes?: string;
  processedByUserId?: string;
  processedByUserName?: string;
  processedAt?: string;
  greenhouseBeds: { bedId: string; bedNumber: string }[];
  locationNotes?: string;
  disposalMethod?: string;
  currentInstructionQuantity: number;
  alreadyDiscarded: number;
  remainingAfterDiscard: number;
}

export const productionDiscardApi = {
  create: async (dto: CreateProductionDiscardDto): Promise<ProductionDiscardDto> => {
    const { data } = await apiClient.post<ApiResponse<ProductionDiscardDto>>(
      "/productiondiscard",
      dto
    );
    return data.data;
  },

  getPendingApprovals: async (
    pageNumber: number,
    pageSize: number,
    search?: string,
    source?: "Greenhouse" | "Agroforestry"
  ): Promise<PagedProductionDiscards> => {
    const params = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    });
    if (search?.trim()) params.set("search", search.trim());
    if (source) params.set("source", source);
    const { data } = await apiClient.get<ApiResponse<PagedProductionDiscards>>(
      `/productiondiscard/pending-approvals?${params.toString()}`
    );
    return data.data;
  },

  getById: async (id: string): Promise<ProductionDiscardDto> => {
    const { data } = await apiClient.get<ApiResponse<ProductionDiscardDto>>(
      `/productiondiscard/${id}`
    );
    return data.data;
  },

  getAvailability: async (
    sowingInstructionId: string
  ): Promise<GreenhouseDiscardAvailabilityDto> => {
    const { data } = await apiClient.get<ApiResponse<GreenhouseDiscardAvailabilityDto>>(
      `/productiondiscard/sowing-instruction/${sowingInstructionId}/availability`
    );
    return data.data;
  },

  getBySowingInstruction: async (sowingInstructionId: string): Promise<ProductionDiscardDto[]> => {
    const { data } = await apiClient.get<ApiResponse<ProductionDiscardDto[]>>(
      `/productiondiscard/sowing-instruction/${sowingInstructionId}`
    );
    return data.data;
  },

  approve: async (id: string, dto: ApproveProductionDiscardDto): Promise<ProductionDiscardDto> => {
    const { data } = await apiClient.post<ApiResponse<ProductionDiscardDto>>(
      `/productiondiscard/${id}/approve`,
      dto
    );
    return data.data;
  },

  process: async (id: string): Promise<ProductionDiscardDto> => {
    const { data } = await apiClient.post<ApiResponse<ProductionDiscardDto>>(
      `/productiondiscard/${id}/process`
    );
    return data.data;
  },
};
